import {
  ArrowRight,
  Check,
  CreditCard,
  Crown,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import { billingAPI } from "../../services/api";
import PlanDetailsDialog from "../dialogs/PlanDetailsDialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

const BillingPage: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [plansResponse, currentPlanResponse] = await Promise.all([
        billingAPI.getPlans(),
        billingAPI.getCurrentPlan(),
      ]);

      setPlans(plansResponse.data);
      setCurrentPlan(currentPlanResponse.data);
    } catch (error) {
      console.error("Failed to load billing data:", error);
      toast({
        title: "Error loading plans",
        description: "Failed to load billing information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan: any) => {
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  // تابع برای بررسی اینکه آیا plan جدید upgrade است
  const isPlanUpgrade = (plan: any): boolean => {
    if (
      !currentPlan ||
      !currentPlan.planName ||
      currentPlan.planName === "free"
    ) {
      return true; // اگر plan فعلی free است، همه plan‌ها upgrade هستند
    }

    // دریافت check_count plan فعلی
    const currentPlanData = plans.find((p) => p.id === currentPlan.planName);
    const currentCheckCount =
      currentPlanData?.checkCount ||
      parseInt(currentPlanData?.productMetadata?.check_count || "0") ||
      0;

    // دریافت check_count plan جدید
    const newCheckCount =
      plan.checkCount ||
      parseInt(plan.productMetadata?.check_count || "0") ||
      0;

    // اگر check_count plan جدید بیشتر باشد، upgrade است
    return newCheckCount > currentCheckCount;
  };

  // تابع برای بررسی اینکه آیا plan برابر با plan فعلی است
  const isCurrentPlan = (plan: any): boolean => {
    return currentPlan?.planName === plan.id;
  };

  // تابع برای بررسی اینکه آیا plan پایین‌تر از plan فعلی است
  const isDowngrade = (plan: any): boolean => {
    if (
      !currentPlan ||
      !currentPlan.planName ||
      currentPlan.planName === "free"
    ) {
      return false;
    }
    return !isCurrentPlan(plan) && !isPlanUpgrade(plan);
  };

  const handleUpgrade = async (plan: any) => {
    try {
      setCheckoutLoading(true);
      const response = await billingAPI.createCheckout({
        priceId: plan.stripePriceId,
      });
      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      console.error("Failed to create checkout:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create checkout session";

      toast({
        title: "Checkout failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return <Zap className="h-6 w-6 text-blue-500" />;
      case "premium":
        return <Star className="h-6 w-6 text-purple-500" />;
      case "enterprise":
        return <Crown className="h-6 w-6 text-emerald-500" />;
      default:
        return <CreditCard className="h-6 w-6 text-gray-500" />;
    }
  };

  const getPlanGradient = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return "from-blue-500 to-blue-600";
      case "premium":
        return "from-purple-500 to-purple-600";
      case "enterprise":
        return "from-emerald-500 to-emerald-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getPlanBorderColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return "border-blue-200 dark:border-blue-800";
      case "premium":
        return "border-purple-200 dark:border-purple-800";
      case "enterprise":
        return "border-emerald-200 dark:border-emerald-800";
      default:
        return "border-gray-200 dark:border-gray-700";
    }
  };

  // Format prices coming in minor units (e.g., cents) like 4900 -> "49"
  const formatPrice = (amountMinor: number) => {
    if (amountMinor == null) return "-";
    const major = amountMinor / 100;
    const asString = major.toFixed(2);
    return asString.replace(/\.00$/, "");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Sparkles className="h-4 w-4" />
            Choose Your Plan
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Subscription Plans
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Select the perfect plan for your identity verification needs. Scale
            as you grow with flexible monthly subscriptions.
          </p>
        </div>

        {/* Current Plan Status */}
        {currentPlan && (
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getPlanIcon(currentPlan.planName)}
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold capitalize">
                    {currentPlan.planName === "free"
                      ? "Free"
                      : currentPlan.planName}
                  </h3>
                  <p className="text-muted-foreground">
                    Status:{" "}
                    {currentPlan.planStatus === "active"
                      ? "Active"
                      : "Inactive"}
                  </p>
                </div>
                <Badge
                  variant={
                    currentPlan.planStatus === "active"
                      ? "default"
                      : "secondary"
                  }
                  className="text-sm"
                >
                  {currentPlan.planStatus === "active" ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                plan.isPopular
                  ? `border-primary bg-gradient-to-br from-primary/5 to-primary/10 ${getPlanBorderColor(
                      plan.name
                    )}`
                  : `border-border ${getPlanBorderColor(plan.name)}`
              }`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  {getPlanIcon(plan.name)}
                </div>
                <CardTitle className="text-2xl font-bold">
                  {plan.name}
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Price */}
                <div className="text-center">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold">
                      ${formatPrice(plan.pricePerMonth)}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {plan.currency.toUpperCase()} • {plan.interval}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Monthly Verifications:
                    </span>
                    <span className="font-semibold">
                      {plan.productMetadata?.check_count ||
                        plan.monthlyLimit ||
                        0}
                    </span>
                  </div>
                  {plan.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        {feature.trim()}
                      </span>
                    </div>
                  ))}
                  {/* Add Bulk Upload feature for Growth and Enterprise */}
                  {(plan.id === "growth" || plan.id === "enterprise") && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        Bulk Employee Upload (CSV Import)
                      </span>
                    </div>
                  )}
                </div>

                {/* Downgrade Warning */}
                {isDowngrade(plan) && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 text-center">
                      ⚠️ This plan is lower than your current plan. You can only
                      upgrade to higher plans.
                    </p>
                  </div>
                )}

                {/* Action Button */}
                <Button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full ${
                    plan.isPopular && !isDowngrade(plan)
                      ? `bg-gradient-to-r ${getPlanGradient(
                          plan.name
                        )} hover:opacity-90`
                      : isDowngrade(plan)
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-primary/90"
                  } text-white font-semibold py-3 transition-all duration-200`}
                  disabled={
                    isCurrentPlan(plan) || isDowngrade(plan) || checkoutLoading
                  }
                >
                  {checkoutLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : isCurrentPlan(plan) ? (
                    "Current Plan"
                  ) : isDowngrade(plan) ? (
                    "Downgrade Not Allowed"
                  ) : (
                    <>
                      {isPlanUpgrade(plan) ? "Upgrade" : "Select"} Plan
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Plan Details Dialog */}
        <PlanDetailsDialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          plan={selectedPlan}
          currentPlan={currentPlan}
          allPlans={plans}
          onUpgrade={handleUpgrade}
        />
      </div>
    </div>
  );
};

export default BillingPage;
